import { Box } from '@chakra-ui/react';
import { useEditor, EditorContent } from '@tiptap/react';
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useField } from 'formik';
import { sanitizeDom } from '@/lib/purify';
import { debounce } from 'lodash-es';
import Placeholder from '@tiptap/extension-placeholder';
import VariableHighlightDecoration from '@/ui/editor/extensions/variable-highlight-decoration';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import TiptapText from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import CharacterCount from '@tiptap/extension-character-count';
import EditorCustomVariables from './EditorCustomVariables';
import { useMutation } from '@tanstack/react-query';
import { getPlaceholderPreview } from './api';
import { SanitizedBox } from '@/ui/SanitizedDom';
import {
  addTooltipToUnsupportedVariables,
  addVariableHoverListeners,
} from './helper';
import { colors } from '@/theme/foundations/colors';

interface PreviewProps {
  enable?: boolean;
  payload?: Record<string, unknown>;
}

interface VariableInlineEditorItem {
  name: string;
  editorContainerStyles?: Record<string, unknown>;
  content?: string;
  editable?: boolean;
  replaceContent?: boolean;
  editorStyles?: CSSProperties;
  enableCustomVariables?: boolean;
  customVariableList?: Record<string, string[]>;
  placeholder?: string;
  getEditorInstance?: boolean;
  customEditorClassName?: string;
  maxLength?: number;
  variableWrapperProps?: Record<string, unknown>;
  showErrorStyles?: boolean;
  isRequired?: boolean;
  disableHoverBg?: boolean;
  templateIsPreview?: boolean;
  previewProps?: PreviewProps;
}

interface VariableInlineEditorProps {
  item?: VariableInlineEditorItem;
  _field?: unknown;
  formik?: any;
}

interface VariableInsertedEventDetail {
  type?: string;
  editorName?: string;
  editorType?: string;
}

export const VariableInlineEditor = ({
  item = {} as VariableInlineEditorItem,
  _field,
  formik,
}: VariableInlineEditorProps) => {
  const {
    name,
    editorContainerStyles = {},
    content = '',
    editable = true,
    replaceContent = true,
    editorStyles = {},
    enableCustomVariables = false,
    customVariableList,
    placeholder = `Enter ${name?.toLowerCase()}`,
    getEditorInstance = false,
    customEditorClassName = '',
    maxLength = 150,
    variableWrapperProps = {},
    showErrorStyles = false,
    isRequired,
    disableHoverBg = false,
    templateIsPreview = false,
    previewProps = {
      enable: false,
    },
  } = item;
  const [_, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const { setValues, values } = formik || {};
  const variablesListArr = Object.entries(customVariableList || {}).flatMap(
    ([, values = []]) => values
  );
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};

  const setEditorValue = (editor) => {
    setValue(editor.getText());
    setError(null);
  };

  const [previewData, setPreviewData] = useState(null);

  const previewMutation = useMutation({
    mutationFn: getPlaceholderPreview,
    onSuccess: (data) => {
      setPreviewData(data);
      setTimeout(() => {
        addVariableHoverListeners();
      }, 0);
    },
  });

  useEffect(() => {
    if (templateIsPreview) {
      const previewPayload = {
        ...{
          body: meta?.value || content,
          retain_variables: true,
        },
        ...previewProps.payload,
      };
      previewMutation.mutate(previewPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateIsPreview]);

  const editor = useEditor({
    extensions: [
      Document.extend({
        content: 'block',
      }),
      History,
      Paragraph.extend({
        addAttributes() {
          return { class: { default: 'single-line' } };
        },
      }),
      TiptapText,
      Placeholder.configure({ placeholder }),
      CharacterCount.configure({
        limit: maxLength,
      }),
      enableCustomVariables &&
        customVariableList &&
        VariableHighlightDecoration.configure({
          enabled: enableCustomVariables,
          supportedVariables: variablesListArr,
        }),
    ],
    editable,
    content: sanitizeDom(content),
    onUpdate: debounce(({ editor }) => {
      setEditorValue(editor);
      setTouched(true); // Mark as touched when user makes changes
    }, 300),
    onCreate({ editor }) {
      editor?.commands?.setContent(meta?.value || content);
      setEditorValue(editor);
    },
  });

  const showError =
    (submitCount > 0 || touched) &&
    (!(value?.toString()?.length > 0) || meta?.error) &&
    !showErrorStyles &&
    isRequired;

  const isDisabled = !editable;
  const hasCustomBorderStyles =
    Object.prototype.hasOwnProperty.call(editorContainerStyles, 'border') ||
    Object.prototype.hasOwnProperty.call(editorContainerStyles, 'borderColor');

  useEffect(() => {
    if (!editor) {
      return undefined;
    }
    editor.setEditable(editable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  useEffect(() => {
    const { editorInstance } = values || {};
    if (!editorInstance && getEditorInstance && editor) {
      setValues({ ...values, editorInstance: editor });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, getEditorInstance]);

  useEffect(() => {
    if (replaceContent) {
      setValue(content);
      editor?.commands?.setContent(content || meta?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, replaceContent]);

  useEffect(() => {
    if (!editor || !customVariableList || !enableCustomVariables) return;
    (editor?.commands as any)?.updateVariablesList?.(variablesListArr);

    // Add focus restoration after variable insertion
    const handleVariableInsertion = () => {
      editor.commands.focus();
    };

    // Listen for custom variable insertion events
    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent<VariableInsertedEventDetail>;
      if (customEvent.detail?.type === 'kula_editor_variable_inserted') {
        const eventEditorName = customEvent.detail?.editorName;
        const eventEditorType = customEvent.detail?.editorType;

        if (eventEditorType === 'input' || eventEditorName === name) {
          handleVariableInsertion();
        }
      }
    };
    document.addEventListener(
      'kula_editor_variable_inserted',
      handleCustomEvent
    );

    return () => {
      document.removeEventListener(
        'kula_editor_variable_inserted',
        handleCustomEvent
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customVariableList, editor]);

  return (
    <>
      <Box
        className={`editor-style variable-inline-editor ${customEditorClassName} `}
        borderRadius="6px"
        overflow="hidden"
        minH="44px"
        py="10px"
        px="12px"
        display="flex"
        gap="5px"
        alignItems="center"
        bg={
          showError
            ? 'pastel-1-light'
            : isDisabled
              ? 'secondary-25'
              : 'base-white'
        }
        color={showError ? 'status-1' : 'secondary-700'}
        cursor={isDisabled ? 'not-allowed' : 'text'}
        _hover={{
          bg: disableHoverBg
            ? showError
              ? 'pastel-1-light'
              : isDisabled
                ? 'secondary-25'
                : 'base-white'
            : showError
              ? 'pastel-1-light'
              : isDisabled
                ? 'secondary-25'
                : 'secondary-25',
          borderColor: showError ? 'status-1' : 'secondary-300',
          boxShadow: 'none',
        }}
        _focusWithin={{
          bg: disableHoverBg
            ? showError
              ? 'pastel-1-light'
              : isDisabled
                ? 'secondary-25'
                : 'base-white'
            : showError
              ? 'pastel-1-light'
              : 'secondary-25',
          borderColor: showError ? 'status-1' : 'secondary-300',
          boxShadow: 'none',
        }}
        border={hasCustomBorderStyles ? undefined : '1px solid'}
        borderColor={
          hasCustomBorderStyles
            ? undefined
            : showError
              ? 'status-1'
              : 'secondary-100'
        }
        transition="background-color 0.15s ease, border-color 0.15s ease"
        {...editorContainerStyles}
      >
        <Box
          style={{
            height: '21px',
            overflow: 'hidden',
            flex: '1',
            ...editorStyles,
          }}
          sx={{
            '.ProseMirror': {
              outline: 'none',
              cursor: isDisabled ? 'not-allowed' : 'text',
            },
            '.ProseMirror p': {
              color: showError
                ? colors['status-1']
                : isDisabled
                  ? colors['secondary-500']
                  : colors['secondary-700'],
            },
            '.ProseMirror p.is-empty::before': {
              color: showError ? colors['status-1'] : colors['secondary-500'],
            },
          }}
        >
          {!templateIsPreview ? (
            <EditorContent
              editor={editor}
              id={name}
              onBlur={() => setTouched(true)}
            />
          ) : (
            <Box>
              <SanitizedBox
                fontSize="14px"
                html={addTooltipToUnsupportedVariables(previewData?.body)}
                color="secondary-700"
              />
            </Box>
          )}
        </Box>
        {enableCustomVariables && (
          <Box
            pointerEvents={templateIsPreview ? 'none' : 'all'}
            opacity={templateIsPreview ? 0.5 : 1}
          >
            <EditorCustomVariables
              config={customVariableList}
              editor={editor}
              wrapperProps={{ pt: '0px', ...variableWrapperProps }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
